/**
 * useSimpleVideoPlayer - Video playback with seamless transitions
 * 
 * YouTube-style approach:
 * - Playhead position is ALWAYS derived from video.currentTime
 * - If video is buffering, playhead waits (stays in sync)
 * - Preloads next clip for seamless transitions
 */

import { useRef, useCallback, useEffect, useState } from 'react';
import { getEffectiveDuration } from '../utils/clipUtils';

export function useSimpleVideoPlayer({
  videoRef,
  preloadVideoRef,
  clips = [],
  shots = [],
  getAssetUrl,
  isPlaying,
  onPositionChange,
  onSeekComplete,
  onClipChange
}) {
  const [currentClipIndex, setCurrentClipIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showBlackScreen, setShowBlackScreen] = useState(false);
  
  const initialLoadDoneRef = useRef(false);
  const blackScreenTimerRef = useRef(null);
  const preloadedIndexRef = useRef(-1);
  const loadClipAtIndexRef = useRef(null);
  const rafIdRef = useRef(null);
  const lastPositionRef = useRef(0);
  
  // Track when playback started for each clip to handle startup delay
  const playStartTimeRef = useRef(null);
  const playStartVideoTimeRef = useRef(null);

  // Get video URL for a clip
  const getClipVideoUrl = useCallback((clip) => {
    if (!clip || clip.is_blank) return null;
    
    const shot = shots.find(s => s.id === clip.scene_id);
    if (!shot) return null;
    
    const takes = shot.takes || [];
    if (takes.length === 0) return null;
    
    let take = null;
    if (shot.printed_take_id) {
      take = takes.find(t => t.id === shot.printed_take_id);
    }
    if (!take && clip.take_id) {
      take = takes.find(t => t.id === clip.take_id);
    }
    if (!take) {
      take = takes.find(t => t.video_url);
    }
    
    return take?.video_url ? getAssetUrl(take.video_url) : null;
  }, [shots, getAssetUrl]);

  // Preload next clip
  const preloadNextClip = useCallback((currentIndex) => {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= clips.length || !preloadVideoRef?.current) return;
    if (preloadedIndexRef.current === nextIndex) return;
    
    const nextClip = clips[nextIndex];
    if (nextClip.is_blank) return;
    
    const url = getClipVideoUrl(nextClip);
    if (!url) return;
    
    console.log('Preloading next clip:', nextIndex);
    preloadVideoRef.current.src = url;
    preloadVideoRef.current.currentTime = (nextClip.trim_start_ms || 0) / 1000;
    preloadVideoRef.current.load();
    preloadedIndexRef.current = nextIndex;
  }, [clips, preloadVideoRef, getClipVideoUrl]);

  // Play black screen for duration then move to next clip
  const playBlackScreen = useCallback((index, duration) => {
    console.log('Playing black screen for', duration, 'ms');
    setShowBlackScreen(true);
    setIsLoading(false);
    setCurrentClipIndex(index);
    
    const clip = clips[index];
    lastPositionRef.current = clip.start_ms;
    onClipChange?.(clip);
    
    if (blackScreenTimerRef.current) {
      cancelAnimationFrame(blackScreenTimerRef.current);
    }
    
    const startTime = performance.now();
    const speed = clip.settings?.speed ?? 1;
    
    const updateBlackScreenPosition = () => {
      const elapsed = performance.now() - startTime;
      const timelinePosition = clip.start_ms + (elapsed * speed);
      
      lastPositionRef.current = timelinePosition;
      onPositionChange?.(timelinePosition);
      
      if (elapsed >= duration / speed) {
        blackScreenTimerRef.current = null;
        setShowBlackScreen(false);
        
        if (index < clips.length - 1) {
          loadClipAtIndexRef.current?.(index + 1);
        }
      } else {
        blackScreenTimerRef.current = requestAnimationFrame(updateBlackScreenPosition);
      }
    };
    
    blackScreenTimerRef.current = requestAnimationFrame(updateBlackScreenPosition);
    preloadNextClip(index);
  }, [clips, onPositionChange, onClipChange, preloadNextClip]);

  // Load a specific clip
  const loadClipAtIndex = useCallback((index) => {
    const video = videoRef?.current;
    if (!video || !clips.length || index < 0 || index >= clips.length) {
      return;
    }
    
    if (blackScreenTimerRef.current) {
      cancelAnimationFrame(blackScreenTimerRef.current);
      blackScreenTimerRef.current = null;
    }
    setShowBlackScreen(false);
    
    const clip = clips[index];
    
    // Handle blank clips
    if (clip.is_blank) {
      const duration = getEffectiveDuration(clip);
      if (isPlaying) {
        playBlackScreen(index, duration);
      } else {
        setCurrentClipIndex(index);
        lastPositionRef.current = clip.start_ms;
        onPositionChange?.(clip.start_ms);
        onClipChange?.(clip);
      }
      return;
    }
    
    const url = getClipVideoUrl(clip);
    if (!url) {
      console.log('No video URL for clip, showing black screen');
      const duration = getEffectiveDuration(clip);
      // Still set clip index and state even when no video
      setCurrentClipIndex(index);
      lastPositionRef.current = clip.start_ms;
      onPositionChange?.(clip.start_ms);
      onClipChange?.(clip);
      
      if (isPlaying) {
        playBlackScreen(index, duration);
      } else {
        setShowBlackScreen(true);
      }
      return;
    }
    
    // Check if this clip is preloaded
    if (preloadedIndexRef.current === index && preloadVideoRef?.current?.src) {
      console.log('Using preloaded video');
      const preloadedSrc = preloadVideoRef.current.src;
      const preloadedTime = preloadVideoRef.current.currentTime;
      video.src = preloadedSrc;
      video.currentTime = preloadedTime;
      preloadVideoRef.current.src = '';
      preloadedIndexRef.current = -1;
      setIsLoading(false);
    } else {
      setIsLoading(true);
      video.src = url;
      video.currentTime = (clip.trim_start_ms || 0) / 1000;
    }
    
    // Apply clip settings
    const settings = clip.settings || {};
    video.volume = Math.max(0, Math.min(1, settings.volume ?? 1));
    video.playbackRate = Math.max(0.25, Math.min(2, settings.speed ?? 1));
    
    setError(null);
    setCurrentClipIndex(index);
    lastPositionRef.current = clip.start_ms;
    
    // Reset playback timing refs for new clip
    playStartTimeRef.current = null;
    playStartVideoTimeRef.current = null;
    
    // Immediately update playhead to clip start position
    onPositionChange?.(clip.start_ms);
    
    onClipChange?.(clip);
    preloadNextClip(index);
  }, [videoRef, preloadVideoRef, clips, getClipVideoUrl, isPlaying, playBlackScreen, preloadNextClip, onPositionChange, onClipChange]);

  loadClipAtIndexRef.current = loadClipAtIndex;

  // RAF loop - Master clock approach: playhead follows wall-clock time, not video
  // This ensures smooth, consistent playhead movement regardless of video buffering
  useEffect(() => {
    let frameCount = 0;
    
    const updatePlayhead = () => {
      if (!isPlaying) {
        rafIdRef.current = null;
        playStartTimeRef.current = null;
        playStartVideoTimeRef.current = null;
        if (lastPositionRef.current > 0) {
          onSeekComplete?.(lastPositionRef.current);
        }
        return;
      }
      
      // Initialize master clock on first frame
      if (playStartTimeRef.current === null) {
        playStartTimeRef.current = performance.now();
        playStartVideoTimeRef.current = lastPositionRef.current; // Start from current playhead position
      }
      
      // Calculate timeline position from wall-clock time (master clock)
      const wallClockElapsed = performance.now() - playStartTimeRef.current;
      const timelinePosition = playStartVideoTimeRef.current + wallClockElapsed;
      
      // Debug logging every 30 frames (~0.5 sec)
      frameCount++;
      const video = videoRef?.current;
      if (frameCount % 30 === 0) {
        console.log('PLAYHEAD DEBUG (master clock):', {
          timelinePosition: timelinePosition.toFixed(0),
          wallClockElapsed: wallClockElapsed.toFixed(0),
          videoCurrentTime: video ? video.currentTime.toFixed(3) : 'no-video',
          lastPosition: lastPositionRef.current.toFixed(0),
          showBlackScreen
        });
      }
      
      // Update playhead position - always update regardless of black screen
      if (Math.abs(timelinePosition - lastPositionRef.current) > 0.1) {
        lastPositionRef.current = timelinePosition;
        onPositionChange?.(timelinePosition);
      }
      
      // Check if we need to load the next clip based on timeline position
      const clipIndex = currentClipIndex;
      if (clipIndex >= 0 && clipIndex < clips.length) {
        const clip = clips[clipIndex];
        const clipEnd = clip.start_ms + getEffectiveDuration(clip);
        
        if (timelinePosition >= clipEnd && clipIndex < clips.length - 1) {
          // Time to load next clip
          loadClipAtIndexRef.current?.(clipIndex + 1);
        }
      }
      
      rafIdRef.current = requestAnimationFrame(updatePlayhead);
    };
    
    if (isPlaying) {
      if (!rafIdRef.current) {
        // Reset master clock when playback begins
        playStartTimeRef.current = null;
        playStartVideoTimeRef.current = null;
        rafIdRef.current = requestAnimationFrame(updatePlayhead);
      }
    } else {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      if (lastPositionRef.current > 0) {
        onSeekComplete?.(lastPositionRef.current);
      }
    }
    
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, [videoRef, isPlaying, currentClipIndex, clips, onPositionChange, onSeekComplete]);

  // Handle video events
  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    const handleCanPlay = () => {
      console.log('Video can play');
      setIsLoading(false);
      if (isPlaying && !showBlackScreen) {
        video.play().catch(e => console.log('Play error:', e.message));
      }
    };

    // Video ended naturally - the RAF loop will handle loading next clip based on timeline position
    const handleEnded = () => {
      console.log('Video ended');
      // Don't load next clip here - let the master clock RAF loop handle it
    };

    const handleError = () => {
      console.log('Video error:', video.error?.message);
      if (isPlaying && currentClipIndex >= 0) {
        const clip = clips[currentClipIndex];
        // Calculate remaining time correctly
        // Video is at currentTime, end is at (duration_ms - trim_end_ms) / 1000
        const clipEndTime = (clip.duration_ms - (clip.trim_end_ms || 0)) / 1000;
        const remaining = (clipEndTime - video.currentTime) * 1000;
        if (remaining > 100) {
          playBlackScreen(currentClipIndex, remaining);
        } else if (currentClipIndex < clips.length - 1) {
          loadClipAtIndex(currentClipIndex + 1);
        }
      } else {
        setError('load-error');
        setIsLoading(false);
      }
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
    };
  }, [videoRef, clips, currentClipIndex, isPlaying, showBlackScreen, loadClipAtIndex, playBlackScreen]);

  // Play/pause based on isPlaying prop
  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    if (isPlaying && !isLoading && !error && !showBlackScreen && video.src) {
      video.play().catch(e => console.log('Play error:', e.message));
    } else if (!isPlaying) {
      video.pause();
      if (blackScreenTimerRef.current) {
        cancelAnimationFrame(blackScreenTimerRef.current);
        blackScreenTimerRef.current = null;
      }
    }
  }, [videoRef, isPlaying, isLoading, error, showBlackScreen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (blackScreenTimerRef.current) {
        cancelAnimationFrame(blackScreenTimerRef.current);
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  // Load first clip when clips become available
  useEffect(() => {
    if (clips.length > 0 && shots.length > 0 && !initialLoadDoneRef.current) {
      console.log('Initial load - clips:', clips.length, 'shots:', shots.length);
      initialLoadDoneRef.current = true;
      loadClipAtIndex(0);
    }
  }, [clips.length, shots.length, loadClipAtIndex]);

  // Seek to position
  const seekToPosition = useCallback((positionMs) => {
    const video = videoRef?.current;
    if (!video || !clips.length) return;
    
    // Find which clip this position falls within
    let targetIndex = 0;
    for (let i = 0; i < clips.length; i++) {
      const clip = clips[i];
      const clipEnd = clip.start_ms + getEffectiveDuration(clip);
      if (positionMs < clipEnd) {
        targetIndex = i;
        break;
      }
      targetIndex = i;
    }
    
    const clip = clips[targetIndex];
    const clipStart = clip.start_ms;
    const offsetInClip = Math.max(0, positionMs - clipStart);
    const speed = clip.settings?.speed ?? 1;
    const videoTime = (clip.trim_start_ms || 0) + (offsetInClip * speed);
    
    // Reset master clock so playback starts from seek position
    playStartTimeRef.current = null;
    playStartVideoTimeRef.current = null;
    
    // Update position FIRST before loading clip
    lastPositionRef.current = positionMs;
    onPositionChange?.(positionMs);
    
    if (targetIndex !== currentClipIndex) {
      // Load the new clip
      const url = getClipVideoUrl(clip);
      
      if (clip.is_blank || !url) {
        // Handle blank or no-video clips
        setShowBlackScreen(true);
        setCurrentClipIndex(targetIndex);
        onClipChange?.(clip);
        return;
      }
      
      setIsLoading(true);
      setShowBlackScreen(false);
      video.src = url;
      video.currentTime = Math.max(0, videoTime / 1000);
      
      const settings = clip.settings || {};
      video.volume = Math.max(0, Math.min(1, settings.volume ?? 1));
      video.playbackRate = Math.max(0.25, Math.min(2, settings.speed ?? 1));
      
      setError(null);
      setCurrentClipIndex(targetIndex);
      onClipChange?.(clip);
      preloadNextClip(targetIndex);
    } else if (!showBlackScreen) {
      video.currentTime = Math.max(0, videoTime / 1000);
    }
  }, [videoRef, clips, currentClipIndex, showBlackScreen, getClipVideoUrl, preloadNextClip, onPositionChange, onClipChange]);

  return {
    currentClip: clips[currentClipIndex] || null,
    currentClipIndex,
    isLoading,
    error,
    showBlackScreen,
    seekToPosition,
    loadClip: loadClipAtIndex
  };
}

export default useSimpleVideoPlayer;
